import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Stack } from '@astryxdesign/core/Stack'
import { Text } from '@astryxdesign/core/Text'
import { Badge } from '@astryxdesign/core/Badge'
import { Button } from '@astryxdesign/core/Button'
import { TextArea } from '@astryxdesign/core/TextArea'
import { Selector } from '@astryxdesign/core/Selector'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { ToggleButton } from '@astryxdesign/core/ToggleButton'
import { db, alive } from '#/lib/db'
import { useMe, usePlaces, useTrip } from '#/lib/hooks'
import { enqueue } from '#/lib/sync'
import { SwipeRow } from '#/components/SwipeRow'

export const Route = createFileRoute('/log')({ component: Log })

/** Notes and links. Audio is deliberately deferred — the phone's own voice
 *  memo app is a fine fallback and this is last in the cut order. */
function Log() {
  const trip = useTrip()
  const places = usePlaces(trip?.id)
  const me = useMe()
  const [tab, setTab] = useState<'notes' | 'links'>('notes')
  const [draft, setDraft] = useState('')
  const [placeId, setPlaceId] = useState<string>('')

  const notes = useLiveQuery(
    async () => (trip ? alive(await db.notes.where('trip_id').equals(trip.id).toArray()) : []),
    [trip?.id], [],
  )
  const links = useLiveQuery(
    async () => (trip ? alive(await db.links.where('trip_id').equals(trip.id).toArray()) : []),
    [trip?.id], [],
  )

  async function add() {
    if (!trip || !me || !draft.trim()) return
    const isUrl = /^https?:\/\//i.test(draft.trim())
    await enqueue({
      table: isUrl ? 'links' : 'notes',
      op: 'upsert',
      row: isUrl
        ? {
            id: crypto.randomUUID(), trip_id: trip.id, place_id: placeId || null,
            url: draft.trim(), title: null,
            kind: /instagram\.com/.test(draft) ? 'reel'
              : /maps\.(google|app)/.test(draft) ? 'map' : 'other',
            created_by: me.id,
          }
        : {
            id: crypto.randomUUID(), trip_id: trip.id, place_id: placeId || null,
            body_md: draft.trim(), pinned: 0, guest_visible: 0, created_by: me.id,
          },
    })
    setDraft('')
  }

  if (!trip) return <Text type="supporting">No trip yet.</Text>

  const placeName = (id: string | null) => places.find((p) => p.id === id)?.name

  return (
    <Stack direction="vertical" gap={4}>
      <SegmentedControl
        label="Log type"
        value={tab}
        onChange={(v) => setTab(v as 'notes' | 'links')}
        layout="fill"
      >
        <SegmentedControlItem value="notes" label="Notes" />
        <SegmentedControlItem value="links" label="Links" />
      </SegmentedControl>

      {/* The list reads from the top, the way a list should. */}
      {tab === 'notes' && (
        <Stack direction="vertical" gap={2}>
          {(notes ?? []).map((n) => (
            <SwipeRow
              key={n.id}
              confirm={`Delete this note`}
              onDelete={() => enqueue({ table: 'notes', op: 'delete', row: { id: n.id } })}
            >
              <Stack direction="vertical" gap={2} padding={3}>
                <Text>{n.body_md}</Text>
                <Stack direction="horizontal" align="center" justify="between" gap={2}>
                  <Text type="supporting">{placeName(n.place_id) ?? 'No place'}</Text>
                  <ToggleButton
                    size="sm"
                    label={n.guest_visible ? 'Shared' : 'Private'}
                    isPressed={!!n.guest_visible}
                    onPressedChange={(pressed) => enqueue({
                      table: 'notes', op: 'upsert',
                      row: { ...n, guest_visible: pressed ? 1 : 0 },
                    })}
                  >
                    <Text size="xsm">{n.guest_visible ? 'Shared' : 'Private'}</Text>
                  </ToggleButton>
                </Stack>
              </Stack>
            </SwipeRow>
          ))}
          {(notes ?? []).length === 0 && (
            <Text type="supporting">Nothing written down yet.</Text>
          )}
        </Stack>
      )}

      {tab === 'links' && (
        <Stack direction="vertical" gap={2}>
          {(links ?? []).map((l) => (
            <SwipeRow
              key={l.id}
              confirm="Delete this link"
              onDelete={() => enqueue({ table: 'links', op: 'delete', row: { id: l.id } })}
            >
              <a className="plain-link" href={l.url} target="_blank" rel="noreferrer noopener">
                <Stack direction="vertical" gap={2} padding={3}>
                  <Text weight="medium" maxLines={2}>{l.title ?? l.url}</Text>
                  <Stack direction="horizontal" align="center" gap={2} wrap="wrap">
                    <Badge label={l.kind} variant="neutral" />
                    <Text type="supporting">{placeName(l.place_id) ?? 'No place'}</Text>
                  </Stack>
                </Stack>
              </a>
            </SwipeRow>
          ))}
          {(links ?? []).length === 0 && (
            <Text type="supporting">No links saved yet.</Text>
          )}
        </Stack>
      )}

      {/*
        The composer is pinned to the bottom of the scroller.

        It used to sit above the list, which on a phone put the one control you
        came to use at the far end of a reach and pushed the list you came to
        read off the screen. Every app built around adding short things —
        Messages, WhatsApp, Slack — docks the field at the bottom instead, in
        the thumb arc and next to the keyboard it raises. This is that, sticky
        rather than fixed, so it rides above the tab bar and never covers the
        last row.
      */}
      <div className="composer">
        <Stack direction="vertical" gap={2} padding={3}>
          <TextArea
            label="New entry"
            isLabelHidden
            value={draft}
            onChange={(v) => setDraft(v)}
            placeholder="A note, or paste a link…"
            rows={2}
          />
          <Stack direction="horizontal" gap={2} align="end">
            <Selector
              label="Place"
              isLabelHidden
              placeholder="No place"
              size="sm"
              variant="ghost"
              presentation="adaptive"
              value={placeId}
              onChange={(v) => setPlaceId(v)}
              options={places.map((p) => ({ value: p.id, label: p.name }))}
            />
            <Button label="Save" variant="primary" isDisabled={!draft.trim()} onClick={add} />
          </Stack>
        </Stack>
      </div>
    </Stack>
  )
}
